<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-20 overflow-hidden">
  <!-- Background Image Overlay -->
  <div class="absolute inset-0 opacity-20">
    <img src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1920&h=800&fit=crop"
         alt="Social Media"
         class="w-full h-full object-cover">
  </div>

  <div class="container mx-auto px-4 relative z-10">
    <!-- Breadcrumb -->
    <nav class="mb-8 text-sm">
      <ol class="flex items-center space-x-2">
        <li><a routerLink="/" class="hover:text-primary-200 transition-colors">Home</a></li>
        <li><span class="material-icons text-xs">chevron_right</span></li>
        <li><a routerLink="/services" class="hover:text-primary-200 transition-colors">Services</a></li>
        <li><span class="material-icons text-xs">chevron_right</span></li>
        <li><a routerLink="/services/performance-marketing" class="hover:text-primary-200 transition-colors">Performance Marketing</a></li>
        <li><span class="material-icons text-xs">chevron_right</span></li>
        <li class="text-primary-200">Social Media Content Management</li>
      </ol>
    </nav>

    <div class="max-w-4xl">
      <h1 class="text-5xl md:text-6xl font-bold mb-6">Social Media Content Management</h1>
      <p class="text-xl md:text-2xl mb-8 text-primary-100">
        Build authentic connections and drive engagement with strategic social media content that resonates with your audience and amplifies your brand voice.
      </p>
      <div class="flex flex-wrap gap-4">
        <a routerLink="/contact" class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all transform hover:scale-105 shadow-lg">
          Start Your Campaign
        </a>
        <a href="#case-study" class="bg-primary-700/50 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-all border-2 border-white/30">
          View Case Study
        </a>
      </div>
    </div>
  </div>

  <!-- Decorative Elements -->
  <div class="absolute top-20 right-10 w-72 h-72 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
  <div class="absolute bottom-20 left-10 w-72 h-72 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
</section>

<!-- Features Section -->
<section class="py-20 bg-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Comprehensive Social Media Services</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        From content creation to community management, we handle every aspect of your social media presence.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <div *ngFor="let feature of features"
           class="bg-gradient-to-br from-primary-50 to-primary-100 p-8 rounded-xl hover:shadow-xl transition-all transform hover:-translate-y-2 border border-primary-100">
        <div class="w-14 h-14 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg flex items-center justify-center mb-6">
          <span class="material-icons text-white text-3xl">{{ feature.icon }}</span>
        </div>
        <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ feature.title }}</h3>
        <p class="text-gray-600 leading-relaxed">{{ feature.description }}</p>
      </div>
    </div>
  </div>
</section>

<!-- Process Section -->
<section class="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Social Media Process</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        A proven methodology for building and growing your social media presence.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <div *ngFor="let step of process; let i = index"
           class="relative bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all">
        <div class="text-6xl font-bold text-primary-200 mb-4">{{ step.step }}</div>
        <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ step.title }}</h3>
        <p class="text-gray-600 leading-relaxed">{{ step.description }}</p>

        <!-- Connector Arrow -->
        <div *ngIf="i < process.length - 1"
             class="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
          <span class="material-icons text-primary-300 text-4xl">arrow_forward</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Case Study Section -->
<section id="case-study" class="py-20 bg-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Success Story</h2>
      <p class="text-xl text-gray-600">Real results from our social media campaigns</p>
    </div>

    <div class="max-w-6xl mx-auto bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl shadow-2xl overflow-hidden border border-primary-100">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">
        <!-- Image -->
        <div class="h-64 lg:h-auto">
          <img [src]="caseStudy.image"
               [alt]="caseStudy.client"
               class="w-full h-full object-cover">
        </div>

        <!-- Content -->
        <div class="p-8 lg:p-12">
          <div class="mb-6">
            <h3 class="text-3xl font-bold text-gray-900 mb-2">{{ caseStudy.client }}</h3>
            <p class="text-primary-600 font-semibold">{{ caseStudy.industry }}</p>
          </div>

          <div class="mb-6">
            <h4 class="text-lg font-bold text-gray-900 mb-2 flex items-center">
              <span class="material-icons text-primary-600 mr-2">flag</span>
              Challenge
            </h4>
            <p class="text-gray-600 leading-relaxed">{{ caseStudy.challenge }}</p>
          </div>

          <div class="mb-6">
            <h4 class="text-lg font-bold text-gray-900 mb-2 flex items-center">
              <span class="material-icons text-primary-600 mr-2">lightbulb</span>
              Solution
            </h4>
            <p class="text-gray-600 leading-relaxed">{{ caseStudy.solution }}</p>
          </div>

          <div>
            <h4 class="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span class="material-icons text-indigo-600 mr-2">trending_up</span>
              Results
            </h4>
            <ul class="space-y-3">
              <li *ngFor="let result of caseStudy.results"
                  class="flex items-start text-gray-700">
                <span class="material-icons text-green-500 mr-2 mt-0.5">check_circle</span>
                <span>{{ result }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Testimonial Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
  <div class="container mx-auto px-4">
    <div class="max-w-4xl mx-auto text-center">
      <div class="mb-8">
        <span class="material-icons text-6xl text-primary-200">format_quote</span>
      </div>

      <blockquote class="text-2xl md:text-3xl font-light mb-12 leading-relaxed">
        "{{ testimonial.quote }}"
      </blockquote>

      <div class="flex items-center justify-center">
        <img [src]="testimonial.image"
             [alt]="testimonial.author"
             class="w-16 h-16 rounded-full border-4 border-white/30 mr-4">
        <div class="text-left">
          <div class="font-bold text-lg">{{ testimonial.author }}</div>
          <div class="text-primary-200">{{ testimonial.role }}, {{ testimonial.company }}</div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-white">
  <div class="container mx-auto px-4">
    <div class="max-w-4xl mx-auto text-center">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
        Ready to Amplify Your Social Media Presence?
      </h2>
      <p class="text-xl text-gray-600 mb-8">
        Let's create a social media strategy that builds community, drives engagement, and delivers measurable results for your business.
      </p>
      <div class="flex flex-wrap justify-center gap-4">
        <a routerLink="/contact"
           class="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 transition-all transform hover:scale-105 shadow-lg inline-flex items-center">
          <span class="material-icons mr-2">rocket_launch</span>
          Get Started Today
        </a>
        <a routerLink="/services/performance-marketing"
           class="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-all inline-flex items-center">
          <span class="material-icons mr-2">arrow_back</span>
          View All Marketing Services
        </a>
      </div>
    </div>
  </div>
</section>
